import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { X } from 'lucide-react';
import { IconMapPin, IconBrandGoogleMaps } from '@tabler/icons-react';
import { useEquipmentSearch } from '../../new-os/hooks/useEquipmentSearch';
import {
  getContractEquipment,
  searchContracts,
} from '../../new-os/services/equipmentService';
import {
  contractCodigoFromHit,
  mapContractEquipmentToLocais,
} from '../../new-os/utils/openOsV2SearchBridge';
import { GoHoverTooltip } from '../../go-config/components/GoHoverTooltip';
import { fetchAdhocOsContext, fetchAdhocOsLocation } from '../../checkout-view/services/checkoutViewService';
import '../../new-os/components/OpenOsV2MockOverlay.css';
import '../../checkout-view/components/CheckoutAdhocOsCreateModal.css';
import './EquipmentConsultSearchModal.css';

const OS_TYPE_CONTRATO = 'contrato';
const MODE_SERIAL = 'serial';
const MODE_CONTRACT = 'contract';
const MODE_TREE = 'tree';

function formatAdhocDateTimeParts(val) {
  if (val == null || val === '') return { date: '—', time: '—' };
  const d = val instanceof Date ? val : new Date(val);
  if (Number.isNaN(d.getTime())) return { date: '—', time: '—' };
  const dd = String(d.getDate()).padStart(2, '0');
  const MM = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return { date: `${dd}/${MM}/${yyyy}`, time: `${hh}:${mm}` };
}

function formatMeterValue(val) {
  if (val == null || val === '' || Number.isNaN(Number(val))) return '—';
  return Math.round(Number(val)).toLocaleString('pt-BR', { maximumFractionDigits: 0 });
}

function SiteNameWithAddress({ nome, endereco }) {
  const wrapRef = useRef(null);
  const [tip, setTip] = useState(null);
  const addr = String(endereco || '').trim();

  useEffect(() => {
    if (!tip) return undefined;
    const hide = () => setTip(null);
    window.addEventListener('scroll', hide, true);
    return () => window.removeEventListener('scroll', hide, true);
  }, [tip]);

  const show = () => {
    if (!addr || !wrapRef.current) return;
    const r = wrapRef.current.getBoundingClientRect();
    setTip({
      text: addr,
      top: Math.max(8, r.top - 8),
      left: r.left + Math.min(r.width / 2, 200),
    });
  };

  return (
    <>
      <div
        ref={wrapRef}
        className="oosv2-site-name"
        onMouseEnter={show}
        onMouseLeave={() => setTip(null)}
      >
        <IconMapPin className="oosv2-site-pin" size={15} stroke={1.7} aria-hidden />
        <span className="oosv2-site-name-text">{nome}</span>
      </div>
      {tip
        ? ReactDOM.createPortal(
            <div
              className="oosv2-site-addr-tip"
              style={{ top: tip.top, left: tip.left }}
              role="tooltip"
            >
              {tip.text}
            </div>,
            document.body
          )
        : null}
    </>
  );
}

/**
 * Busca por série/patrimônio (mesma API da abertura de OS, tipo contrato), sem checagem de OS aberta.
 * Com allowContractSearch: se série não achar, permite buscar por cliente/contrato e árvore (seleção única).
 */
const EquipmentConsultSearchModal = ({
  open,
  onClose,
  onSelectEquipment,
  initialSerial = '',
  title = 'Consultar equipamento',
  plainHeader = false,
  autoSelectOnFind = false,
  allowContractSearch = false,
  closeOnOverlayClick = true,
  adhocOperationCode = '',
}) => {
  const {
    serialSearch,
    setSerialSearch,
    suggestions,
    setSuggestions,
    isSearching,
    hasSearched,
    setHasSearched,
    suppressNoResultsMessage,
    setSuppressNoResultsMessage,
    isEditingStep1Ref,
    previousEquipmentRef,
    searchInitiatedByEditRef,
    lastSuccessfulSearchRef,
    equipmentCacheRef,
    serialInputRef,
    searchSerialNumber
  } = useEquipmentSearch();

  const autoSelectedRef = useRef(false);
  const contractSearchTimerRef = useRef(null);
  const contractSearchSeqRef = useRef(0);

  const [searchMode, setSearchMode] = useState(MODE_SERIAL);
  const [contractQuery, setContractQuery] = useState('');
  const [contractHits, setContractHits] = useState([]);
  const [contractSearching, setContractSearching] = useState(false);
  const [contractHasSearched, setContractHasSearched] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [locais, setLocais] = useState([]);
  const [locaisLoading, setLocaisLoading] = useState(false);
  const [filtroTree, setFiltroTree] = useState('');
  const [abertosLocais, setAbertosLocais] = useState([]);
  const [abertosModelos, setAbertosModelos] = useState([]);
  const [adhocPreview, setAdhocPreview] = useState(null);
  const [adhocPreviewLoading, setAdhocPreviewLoading] = useState(false);
  const [adhocLocationMap, setAdhocLocationMap] = useState(null);
  const [adhocLocationLoading, setAdhocLocationLoading] = useState(false);
  const [adhocLocationError, setAdhocLocationError] = useState('');

  const resetContractState = useCallback(() => {
    setContractQuery('');
    setContractHits([]);
    setContractSearching(false);
    setContractHasSearched(false);
    setSelectedClient(null);
    setLocais([]);
    setLocaisLoading(false);
    setFiltroTree('');
    setAbertosLocais([]);
    setAbertosModelos([]);
  }, []);

  useEffect(() => {
    if (!open) return;
    autoSelectedRef.current = false;
    setSearchMode(MODE_SERIAL);
    resetContractState();
    const initial = String(initialSerial ?? '')
      .replace(/\s+/g, ' ')
      .trimStart()
      .toUpperCase();
    setSerialSearch(initial);
    setSuggestions([]);
    setHasSearched(false);
    setSuppressNoResultsMessage(false);
    isEditingStep1Ref.current = false;
    previousEquipmentRef.current = null;
    searchInitiatedByEditRef.current = false;
    lastSuccessfulSearchRef.current = null;
    equipmentCacheRef.current = { prefix: '', type: '', allResults: [] };

    if (initial.length >= 4) {
      const timer = setTimeout(() => {
        searchSerialNumber(initial, OS_TYPE_CONTRATO);
      }, 80);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [open, initialSerial, setSerialSearch, setSuggestions, setHasSearched, setSuppressNoResultsMessage, isEditingStep1Ref, previousEquipmentRef, searchInitiatedByEditRef, lastSuccessfulSearchRef, equipmentCacheRef, searchSerialNumber, resetContractState]);

  useEffect(() => {
    if (!open || !adhocOperationCode) {
      setAdhocPreview(null);
      setAdhocPreviewLoading(false);
      setAdhocLocationMap(null);
      setAdhocLocationLoading(false);
      setAdhocLocationError('');
      return undefined;
    }
    const token = localStorage.getItem('authToken');
    if (!token) {
      setAdhocPreview(null);
      return undefined;
    }
    let cancelled = false;
    setAdhocPreviewLoading(true);
    fetchAdhocOsContext(token, adhocOperationCode)
      .then((data) => {
        if (cancelled) return;
        setAdhocPreview({
          operationCode: data?.operationCode || adhocOperationCode || '',
          reason: data?.reason || '',
          requester: data?.requester || '',
          technicalReport: data?.technicalReport || '',
          startedAt: data?.startedAt ?? null,
          finishedAt: data?.finishedAt ?? null,
          meters: Array.isArray(data?.meters) ? data.meters : [],
          gpsLatitude: data?.gpsLatitude ?? null,
          gpsLongitude: data?.gpsLongitude ?? null,
        });
      })
      .catch(() => {
        if (!cancelled) setAdhocPreview(null);
      })
      .finally(() => {
        if (!cancelled) setAdhocPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, adhocOperationCode]);

  const handleOpenAdhocLocation = useCallback(async () => {
    const op = String(adhocPreview?.operationCode || adhocOperationCode || '').trim();
    if (!op || adhocLocationLoading) return;
    const token = localStorage.getItem('authToken');
    if (!token) {
      setAdhocLocationError('Sessão inválida');
      return;
    }
    setAdhocLocationError('');
    setAdhocLocationLoading(true);
    try {
      const data = await fetchAdhocOsLocation(token, op);
      const lat = Number(data?.lat);
      const lng = Number(data?.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        throw new Error('Coordenadas inválidas');
      }
      setAdhocLocationMap({
        operationCode: data?.operationCode || op,
        lat,
        lng,
        address: String(data?.address || `${lat}, ${lng}`).trim(),
      });
    } catch (err) {
      setAdhocLocationError(err?.message || 'Não foi possível obter a localização');
    } finally {
      setAdhocLocationLoading(false);
    }
  }, [
    adhocPreview?.operationCode,
    adhocOperationCode,
    adhocLocationLoading,
  ]);

  useEffect(() => {
    if (!open || autoSelectOnFind || searchMode !== MODE_SERIAL) return;
    const t = setTimeout(() => serialInputRef.current?.focus?.(), 80);
    return () => clearTimeout(t);
  }, [open, autoSelectOnFind, searchMode, serialInputRef]);

  useEffect(() => {
    if (!open || searchMode !== MODE_SERIAL) return;
    if (isEditingStep1Ref.current || suppressNoResultsMessage || searchInitiatedByEditRef.current) return;
    if (serialSearch.length >= 4) {
      const timer = setTimeout(() => {
        if (isEditingStep1Ref.current || suppressNoResultsMessage || searchInitiatedByEditRef.current) return;
        searchSerialNumber(serialSearch, OS_TYPE_CONTRATO);
      }, 300);
      return () => clearTimeout(timer);
    }
    if (!isEditingStep1Ref.current && !suppressNoResultsMessage && !searchInitiatedByEditRef.current) {
      setSuggestions([]);
      setHasSearched(false);
    }
  }, [open, searchMode, serialSearch, searchSerialNumber, suppressNoResultsMessage, isEditingStep1Ref, searchInitiatedByEditRef, setSuggestions, setHasSearched]);

  useEffect(() => {
    if (!open || searchMode !== MODE_CONTRACT) return undefined;
    clearTimeout(contractSearchTimerRef.current);
    const q = contractQuery.trim();
    if (q.length < 4) {
      setContractHits([]);
      setContractSearching(false);
      setContractHasSearched(false);
      return undefined;
    }
    setContractSearching(true);
    const seq = ++contractSearchSeqRef.current;
    contractSearchTimerRef.current = setTimeout(async () => {
      try {
        const result = await searchContracts(q);
        if (seq !== contractSearchSeqRef.current) return;
        const hits = (result?.suggestions || []).map((c) => ({
          kind: 'cliente',
          codigo: c.codigo,
          nome: c.classif || c.nome || '',
          classif: c.classif || c.nome || '',
          titulo: c.classif || c.codigo,
          sub: c.codigo && c.classif && c.classif !== c.codigo ? c.codigo : c.nome || '',
          raw: c,
        }));
        setContractHits(hits);
        setContractHasSearched(true);
      } catch {
        if (seq === contractSearchSeqRef.current) {
          setContractHits([]);
          setContractHasSearched(true);
        }
      } finally {
        if (seq === contractSearchSeqRef.current) setContractSearching(false);
      }
    }, 300);
    return () => clearTimeout(contractSearchTimerRef.current);
  }, [open, searchMode, contractQuery]);

  const handlePick = (suggestion) => {
    if (!suggestion?.serie) return;
    onSelectEquipment?.(suggestion);
  };

  useEffect(() => {
    if (!open || !autoSelectOnFind || searchMode !== MODE_SERIAL) return;
    if (isSearching || !hasSearched) return;
    if (suggestions.length !== 1) return;
    if (autoSelectedRef.current) return;
    const match = suggestions[0];
    if (!match?.serie) return;
    autoSelectedRef.current = true;
    onSelectEquipment?.(match);
  }, [open, autoSelectOnFind, searchMode, isSearching, hasSearched, suggestions, onSelectEquipment]);

  const handleInputChange = (e) => {
    const value = e.target.value.replace(/\s+/g, ' ').trimStart().toUpperCase();
    setSerialSearch(value);
    setSuppressNoResultsMessage(false);
    isEditingStep1Ref.current = false;
    previousEquipmentRef.current = null;
    searchInitiatedByEditRef.current = false;
    lastSuccessfulSearchRef.current = null;
    autoSelectedRef.current = false;
  };

  const enterContractMode = () => {
    setSearchMode(MODE_CONTRACT);
    resetContractState();
    setTimeout(() => serialInputRef.current?.focus?.(), 80);
  };

  const backToSerialMode = () => {
    setSearchMode(MODE_SERIAL);
    resetContractState();
    autoSelectedRef.current = false;
  };

  const backToContractSearch = () => {
    setSearchMode(MODE_CONTRACT);
    setSelectedClient(null);
    setLocais([]);
    setFiltroTree('');
    setAbertosLocais([]);
    setAbertosModelos([]);
  };

  const selectContract = async (hit) => {
    const codigo = contractCodigoFromHit(hit);
    const cliente = {
      codigo,
      nome: hit?.nome || hit?.classif || '',
      classif: hit?.classif || hit?.nome || '',
      titulo: hit?.titulo || '',
      sub: hit?.sub || '',
    };
    setSelectedClient(cliente);
    setContractHits([]);
    setSearchMode(MODE_TREE);
    setLocaisLoading(true);
    setLocais([]);
    setAbertosLocais([]);
    setAbertosModelos([]);
    setFiltroTree('');
    if (!codigo) {
      setLocaisLoading(false);
      return;
    }
    try {
      const result = await getContractEquipment(codigo);
      const mapped = mapContractEquipmentToLocais(result?.equipment || []);
      setLocais(mapped);
    } catch {
      setLocais([]);
    } finally {
      setLocaisLoading(false);
    }
  };

  const toggleIn = (list, key, setter) => {
    setter(list.includes(key) ? list.filter((x) => x !== key) : [...list, key]);
  };

  const pickSerieFromTree = (sr) => {
    const raw = sr?.raw || {};
    const suggestion = {
      serie: raw.serie || sr.serie || '',
      patrimonio: raw.patrimonio || sr.patrimonio || '',
      produto: raw.produto || raw.modelo || '',
      codigo: raw.codigo || selectedClient?.codigo || '',
      codprod: raw.codprod || raw.codProduto || null,
      ...raw,
    };
    if (!suggestion.serie) return;
    onSelectEquipment?.(suggestion);
  };

  const arvore = useMemo(() => {
    const f = filtroTree.trim().toUpperCase();
    return (locais || [])
      .map((l) => {
        const modelos = (l.modelos || [])
          .map((m) => {
            const series = (m.series || []).filter(
              (sr) =>
                !f ||
                `${sr.serie} ${sr.patrimonio}`.toUpperCase().includes(f) ||
                m.nome.toUpperCase().includes(f) ||
                l.nome.toUpperCase().includes(f)
            );
            return { nome: m.nome, series };
          })
          .filter((m) => m.series.length > 0);
        return { nome: l.nome, modelos, endereco: l.endereco || '' };
      })
      .filter((l) => l.modelos.length > 0);
  }, [locais, filtroTree]);

  if (!open) return null;

  const showSerialEmpty =
    searchMode === MODE_SERIAL &&
    hasSearched &&
    suggestions.length === 0 &&
    !isSearching &&
    serialSearch.length >= 4;

  const noSerialIdentified =
    !!adhocOperationCode &&
    searchMode === MODE_SERIAL &&
    !String(initialSerial || '').trim() &&
    !String(serialSearch || '').trim();

  // Mantém layout avulso (equip + motivo/laudo) durante toda a busca por série
  const showAdhocSerialLayout =
    !!adhocOperationCode && searchMode === MODE_SERIAL;

  const startedParts = formatAdhocDateTimeParts(adhocPreview?.startedAt);
  const finishedParts = formatAdhocDateTimeParts(adhocPreview?.finishedAt);

  const dialogClass =
    `equipment-consult-search-dialog` +
    (searchMode === MODE_TREE ? ' equipment-consult-search-dialog--wide' : '') +
    (showAdhocSerialLayout ? ' equipment-consult-search-dialog--adhoc-preview' : '');

  const overlay = (
    <div
      className="modal-overlay"
      role="presentation"
      onClick={closeOnOverlayClick ? onClose : undefined}
      style={{ zIndex: 10002 }}
    >
      <div
        className={dialogClass}
        role="dialog"
        aria-modal="true"
        aria-labelledby="equipment-consult-search-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className={`equipment-consult-search-header${plainHeader ? ' equipment-consult-search-header--plain' : ''}`}
        >
          {plainHeader ? (
            <>
              <div className="equipment-consult-search-header-top">
                <div className="equipment-consult-search-brand">
                  <div
                    id="equipment-consult-search-title"
                    className="equipment-consult-search-context"
                  >
                    {title}
                  </div>
                </div>
                <div className="equipment-consult-search-actions">
                  <button
                    type="button"
                    className="equipment-consult-search-close equipment-consult-search-close--flow"
                    onClick={onClose}
                    aria-label="Fechar"
                  >
                    ×
                  </button>
                </div>
              </div>
              <div className="equipment-consult-search-progress" aria-hidden="true" />
            </>
          ) : (
            <>
              <h2
                id="equipment-consult-search-title"
                className="equipment-consult-search-title"
              >
                {title}
              </h2>
              <button
                type="button"
                className="equipment-consult-search-close"
                onClick={onClose}
                aria-label="Fechar"
              >
                <X size={20} />
              </button>
            </>
          )}
        </div>

        <div
          className={`equipment-consult-search-body${
            showAdhocSerialLayout && suggestions.length > 0
              ? ' equipment-consult-search-body--suggestions-open'
              : ''
          }`}
        >
          {showAdhocSerialLayout ? (
            <div className="equipment-consult-adhoc-preview checkout-adhoc-os-create-section">
              <div className="equipment-consult-adhoc-equip-block">
                <div className="checkout-adhoc-os-create-equip-grid">
                  <div className="checkout-adhoc-os-create-field">
                    <div className="oosv2-equip-kicker">Equipamento</div>
                    <div className="checkout-adhoc-os-create-field-value equipment-consult-adhoc-value--missing">
                      Não encontrado
                    </div>
                  </div>
                  <div className="checkout-adhoc-os-create-field">
                    <div className="oosv2-equip-kicker">Série / Patrimônio</div>
                    <div className="checkout-adhoc-os-create-field-value checkout-adhoc-os-create-field-value--mono equipment-consult-adhoc-value--missing">
                      Não encontrado
                    </div>
                  </div>
                </div>

                <label className="equipment-consult-search-label">
                  Série ou patrimônio
                </label>
                <div className="equipment-consult-search-input-wrap equipment-consult-search-input-wrap--adhoc">
                  <input
                    ref={serialInputRef}
                    type="text"
                    value={serialSearch}
                    onChange={handleInputChange}
                    placeholder="Pesquise aqui..."
                    className={`equipment-consult-search-input${isSearching ? ' equipment-consult-search-input--busy' : ''}`}
                  />
                  {isSearching && (
                    <div className="equipment-consult-search-spinner" aria-hidden />
                  )}
                  {suggestions.length > 0 && (
                    <div className="equipment-consult-search-suggestions equipment-consult-search-suggestions--overlay">
                      {suggestions.map((suggestion, index) => (
                        <div
                          key={`${suggestion.serie}-${suggestion.patrimonio}-${index}`}
                          className="equipment-consult-search-suggestion"
                          onClick={() => handlePick(suggestion)}
                        >
                          <div className="equipment-consult-search-suggestion-title">
                            {suggestion.serie}
                            {suggestion.patrimonio ? ` • ${suggestion.patrimonio}` : ''}
                          </div>
                          <div className="equipment-consult-search-suggestion-meta">{suggestion.produto}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {serialSearch.length > 0 && serialSearch.length < 4 && (
                  <p className="equipment-consult-search-hint">
                    Digite mais {4 - serialSearch.length} caractere(s) para buscar
                  </p>
                )}

                {showSerialEmpty || noSerialIdentified ? (
                  <>
                    <div className="equipment-consult-search-empty">
                      {noSerialIdentified
                        ? 'Atendimento sem série/patrimônio identificado.'
                        : 'Nenhum equipamento encontrado com esta série/patrimônio, pesquise novamente acima.'}
                    </div>
                    {allowContractSearch ? (
                      <div className="equipment-consult-search-contract-cta">
                        <GoHoverTooltip
                          text="Pesquise pelo cliente ou número do contrato para localizar o equipamento"
                          wrap
                        >
                          <button
                            type="button"
                            className="equipment-consult-search-contract-btn"
                            onClick={enterContractMode}
                          >
                            Buscar por contrato
                          </button>
                        </GoHoverTooltip>
                      </div>
                    ) : null}
                  </>
                ) : null}
              </div>

              {adhocPreviewLoading ? (
                <p className="equipment-consult-search-hint">Carregando dados do atendimento…</p>
              ) : (
                <>
                  <div className="checkout-adhoc-os-create-reason-block">
                    <div className="oosv2-equip-kicker">Motivo informado pelo técnico</div>
                    <input
                      type="text"
                      className="checkout-adhoc-os-create-reason-input"
                      value={adhocPreview?.reason || '—'}
                      readOnly
                      tabIndex={-1}
                      aria-label="Motivo informado pelo técnico"
                    />
                  </div>

                  <div className="checkout-adhoc-os-create-closing-preview">
                    <div className="checkout-adhoc-os-create-meta-row">
                      <div className="checkout-adhoc-os-create-field">
                        <div className="oosv2-equip-kicker">Solicitante</div>
                        <div className="checkout-adhoc-os-create-field-value">
                          {adhocPreview?.requester || '—'}
                        </div>
                      </div>
                      <div className="checkout-adhoc-os-create-field checkout-adhoc-os-create-field--attend">
                        <div className="oosv2-equip-kicker">Tempo de atendimento</div>
                        <div className="checkout-adhoc-os-create-attend-row">
                          <span className="checkout-adhoc-os-create-field-value">
                            {startedParts.date}
                          </span>
                          <span className="checkout-adhoc-os-create-attend-time">
                            {startedParts.time}
                          </span>
                          <span className="checkout-adhoc-os-create-attend-sep" aria-hidden>
                            →
                          </span>
                          <span className="checkout-adhoc-os-create-field-value">
                            {finishedParts.date}
                          </span>
                          <span className="checkout-adhoc-os-create-attend-time">
                            {finishedParts.time}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="checkout-adhoc-os-create-field checkout-adhoc-os-create-field--laudo">
                      <div className="oosv2-equip-kicker">Laudo técnico</div>
                      <div
                        className="checkout-adhoc-os-create-laudo"
                        aria-label="Laudo técnico"
                      >
                        <div className="checkout-adhoc-os-create-laudo-row">
                          <span className="checkout-adhoc-os-create-laudo-label">OS Avulsa:</span>
                          <span className="checkout-adhoc-os-create-laudo-value checkout-adhoc-os-create-field-value--mono">
                            {adhocPreview?.operationCode || adhocOperationCode || '—'}
                          </span>
                        </div>
                        <div className="checkout-adhoc-os-create-laudo-row">
                          <span className="checkout-adhoc-os-create-laudo-label">Laudo:</span>
                          <span className="checkout-adhoc-os-create-laudo-value">
                            {adhocPreview?.technicalReport || '—'}
                          </span>
                        </div>
                        <div className="checkout-adhoc-os-create-laudo-row checkout-adhoc-os-create-laudo-row--meters">
                          <span className="checkout-adhoc-os-create-laudo-label">Contadores:</span>
                          <div className="checkout-adhoc-os-create-laudo-meters">
                            {(adhocPreview?.meters || []).length > 0 ? (
                              (adhocPreview.meters || []).map((m, i) => (
                                <div
                                  key={`${m.name}-${i}`}
                                  className="checkout-adhoc-os-create-laudo-meter"
                                >
                                  <span>{m.name || '—'}</span>
                                  <span className="checkout-adhoc-os-create-laudo-meter-sep">
                                    {' '}
                                    -{' '}
                                  </span>
                                  <span className="checkout-adhoc-os-create-field-value--mono">
                                    {formatMeterValue(m.value)}
                                  </span>
                                </div>
                              ))
                            ) : (
                              <span className="checkout-adhoc-os-create-laudo-value">—</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="equipment-consult-adhoc-location-row">
                      <button
                        type="button"
                        className="equipment-consult-adhoc-location-btn"
                        onClick={handleOpenAdhocLocation}
                        disabled={adhocLocationLoading}
                      >
                        <IconMapPin size={16} stroke={1.8} aria-hidden />
                        <span>
                          {adhocLocationLoading
                            ? 'Carregando localização…'
                            : 'Localização do atendimento'}
                        </span>
                      </button>
                      {adhocLocationError ? (
                        <p className="equipment-consult-adhoc-location-error" role="alert">
                          {adhocLocationError}
                        </p>
                      ) : null}
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : null}

          {!showAdhocSerialLayout && searchMode === MODE_SERIAL ? (
            <>
              <label className="equipment-consult-search-label">
                Série ou patrimônio
              </label>
              <div className="equipment-consult-search-input-wrap">
                <input
                  ref={serialInputRef}
                  type="text"
                  value={serialSearch}
                  onChange={handleInputChange}
                  placeholder="Pesquise aqui..."
                  className={`equipment-consult-search-input${isSearching ? ' equipment-consult-search-input--busy' : ''}`}
                />
                {isSearching && (
                  <div className="equipment-consult-search-spinner" aria-hidden />
                )}
              </div>
              {serialSearch.length > 0 && serialSearch.length < 4 && (
                <p className="equipment-consult-search-hint">
                  Digite mais {4 - serialSearch.length} caractere(s) para buscar
                </p>
              )}

              {suggestions.length > 0 && (
                <div className="equipment-consult-search-suggestions">
                  {suggestions.map((suggestion, index) => (
                    <div
                      key={`${suggestion.serie}-${suggestion.patrimonio}-${index}`}
                      className="equipment-consult-search-suggestion"
                      onClick={() => handlePick(suggestion)}
                    >
                      <div className="equipment-consult-search-suggestion-title">
                        {suggestion.serie}
                        {suggestion.patrimonio ? ` • ${suggestion.patrimonio}` : ''}
                      </div>
                      <div className="equipment-consult-search-suggestion-meta">{suggestion.produto}</div>
                    </div>
                  ))}
                </div>
              )}

              {showSerialEmpty || noSerialIdentified ? (
                <>
                  <div className="equipment-consult-search-empty">
                    {noSerialIdentified
                      ? 'Atendimento sem série/patrimônio identificado.'
                      : 'Nenhum equipamento encontrado com esta série/patrimônio.'}
                  </div>
                  {allowContractSearch ? (
                    <div className="equipment-consult-search-contract-cta">
                      <GoHoverTooltip
                        text="Pesquise pelo cliente ou número do contrato para localizar o equipamento"
                        wrap
                      >
                        <button
                          type="button"
                          className="equipment-consult-search-contract-btn"
                          onClick={enterContractMode}
                        >
                          Buscar por contrato
                        </button>
                      </GoHoverTooltip>
                    </div>
                  ) : null}
                </>
              ) : null}
            </>
          ) : null}

          {searchMode === MODE_CONTRACT ? (
            <>
              <div className="equipment-consult-search-mode-bar">
                <button
                  type="button"
                  className="equipment-consult-search-back"
                  onClick={backToSerialMode}
                >
                  Voltar
                </button>
                <span className="equipment-consult-search-mode-label">Busca por contrato</span>
              </div>
              <label className="equipment-consult-search-label">
                Cliente ou contrato
              </label>
              <div className="equipment-consult-search-input-wrap">
                <input
                  ref={serialInputRef}
                  type="text"
                  value={contractQuery}
                  onChange={(e) =>
                    setContractQuery(e.target.value.replace(/\s+/g, ' ').trimStart().toUpperCase())
                  }
                  placeholder="Pesquise o cliente ou contrato…"
                  className={`equipment-consult-search-input${contractSearching ? ' equipment-consult-search-input--busy' : ''}`}
                />
                {contractSearching && (
                  <div className="equipment-consult-search-spinner" aria-hidden />
                )}
              </div>
              {contractQuery.length > 0 && contractQuery.length < 4 && (
                <p className="equipment-consult-search-hint">
                  Digite mais {4 - contractQuery.length} caractere(s) para buscar
                </p>
              )}
              {contractHits.length > 0 && (
                <div className="equipment-consult-search-suggestions">
                  {contractHits.map((hit, index) => (
                    <div
                      key={`${hit.codigo}-${index}`}
                      className="equipment-consult-search-suggestion"
                      onClick={() => selectContract(hit)}
                    >
                      <div className="equipment-consult-search-suggestion-title">{hit.titulo}</div>
                      {hit.sub ? (
                        <div className="equipment-consult-search-suggestion-meta">{hit.sub}</div>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}
              {contractHasSearched &&
                contractHits.length === 0 &&
                !contractSearching &&
                contractQuery.length >= 4 && (
                  <div className="equipment-consult-search-empty">
                    Nenhum cliente/contrato encontrado.
                  </div>
                )}
            </>
          ) : null}

          {searchMode === MODE_TREE ? (
            <div className="open-os-v2-mock-host equipment-consult-search-tree-host">
              <div className="equipment-consult-search-mode-bar">
                <button
                  type="button"
                  className="equipment-consult-search-back"
                  onClick={backToContractSearch}
                >
                  Voltar
                </button>
                <span className="equipment-consult-search-mode-label">Selecionar equipamento</span>
              </div>

              <div className="oosv2-selecao-wrap">
                <div className="oosv2-selecao-toolbar">
                  <div>
                    <div className="oosv2-selecao-title">Selecionar equipamento</div>
                    <div className="oosv2-selecao-subtitle">
                      {selectedClient?.titulo || selectedClient?.nome || selectedClient?.codigo || '—'}
                    </div>
                  </div>
                  <div className="oosv2-selecao-toolbar-spacer" />
                  <input
                    type="text"
                    className="oosv2-selecao-filtro"
                    value={filtroTree}
                    onChange={(e) => setFiltroTree(e.target.value)}
                    placeholder="Filtrar série, patrimônio ou modelo…"
                  />
                  <div className="oosv2-selecao-count">seleção única</div>
                </div>

                {locaisLoading ? (
                  <div className="equipment-consult-search-hint">Carregando equipamentos…</div>
                ) : (
                  <div className="oosv2-selecao-tree">
                    {arvore.map((local) => {
                      const abertoL = abertosLocais.includes(local.nome);
                      const totalEq = local.modelos.reduce((n, m) => n + m.series.length, 0);
                      return (
                        <div key={local.nome} className="oosv2-selecao-local-group">
                          <div
                            className="oosv2-selecao-local-head"
                            onClick={() => toggleIn(abertosLocais, local.nome, setAbertosLocais)}
                          >
                            <div
                              className={`oosv2-selecao-caret${abertoL ? ' is-open' : ''}`}
                              aria-hidden
                            >
                              <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                                <path d="M4.2 2.6L8 6l-3.8 3.4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </div>
                            <SiteNameWithAddress nome={local.nome} endereco={local.endereco} />
                            <div className="oosv2-selecao-meta">
                              {totalEq} equipamento{totalEq === 1 ? '' : 's'}
                            </div>
                          </div>
                          {abertoL
                            ? local.modelos.map((mod) => {
                                const key = `${local.nome}|${mod.nome}`;
                                const abertoM = abertosModelos.includes(key);
                                return (
                                  <div key={key} className="oosv2-selecao-modelo-group">
                                    <div
                                      className="oosv2-selecao-modelo-head"
                                      onClick={() =>
                                        toggleIn(abertosModelos, key, setAbertosModelos)
                                      }
                                    >
                                      <div
                                        className={`oosv2-selecao-caret is-sub${abertoM ? ' is-open' : ''}`}
                                        aria-hidden
                                      >
                                        <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                                          <path d="M4.2 2.6L8 6l-3.8 3.4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                      </div>
                                      <div className="oosv2-selecao-modelo-name">{mod.nome}</div>
                                      <div className="oosv2-selecao-meta">
                                        {mod.series.length} série{mod.series.length === 1 ? '' : 's'}
                                      </div>
                                    </div>
                                    {abertoM
                                      ? mod.series.map((sr) => (
                                          <div
                                            key={`${sr.serie}-${sr.patrimonio}`}
                                            className="oosv2-selecao-serie-row equipment-consult-search-serie-row"
                                            onClick={() => pickSerieFromTree(sr)}
                                          >
                                            <div className="oosv2-selecao-serie-field">
                                              Série{' '}
                                              <span className="oosv2-selecao-serie-val">{sr.serie}</span>
                                            </div>
                                            <div className="oosv2-selecao-serie-field">
                                              Patrimônio{' '}
                                              <span className="oosv2-selecao-serie-val">
                                                {sr.patrimonio || '—'}
                                              </span>
                                            </div>
                                          </div>
                                        ))
                                      : null}
                                  </div>
                                );
                              })
                            : null}
                        </div>
                      );
                    })}
                    {!locaisLoading && arvore.length === 0 ? (
                      <div className="oosv2-selecao-empty">
                        {filtroTree.trim()
                          ? `Nada encontrado para “${filtroTree.trim()}”.`
                          : 'Nenhum equipamento neste contrato.'}
                      </div>
                    ) : null}
                  </div>
                )}

                <div className="oosv2-selecao-foot">
                  <div className="oosv2-selecao-foot-hint">
                    Clique em uma série para selecionar o equipamento (apenas um).
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );

  const mapsEmbedUrl =
    adhocLocationMap &&
    Number.isFinite(adhocLocationMap.lat) &&
    Number.isFinite(adhocLocationMap.lng)
      ? `https://www.google.com/maps?q=${encodeURIComponent(
          `${adhocLocationMap.lat},${adhocLocationMap.lng}`
        )}&output=embed`
      : '';

  const mapsOpenUrl =
    adhocLocationMap &&
    Number.isFinite(adhocLocationMap.lat) &&
    Number.isFinite(adhocLocationMap.lng)
      ? `https://www.google.com/maps?q=${encodeURIComponent(
          `${adhocLocationMap.lat},${adhocLocationMap.lng}`
        )}`
      : '';

  const locationMapOverlay =
    adhocLocationMap && mapsEmbedUrl ? (
      <div
        className="modal-overlay checkout-view-endereco-map-overlay"
        style={{ zIndex: 10008 }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="equipment-consult-adhoc-location-map-title"
        onClick={() => setAdhocLocationMap(null)}
      >
        <div
          className="checkout-view-endereco-map-modal"
          style={{ width: '70vw', height: '70vh' }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="checkout-view-endereco-map-header">
            <div className="checkout-view-endereco-map-header-info">
              <span className="checkout-view-endereco-map-os">
                OS {adhocLocationMap.operationCode}
              </span>
              <span className="checkout-view-endereco-map-cliente">Atendimento avulso</span>
            </div>
            <button
              type="button"
              className="modal-close-button"
              onClick={() => setAdhocLocationMap(null)}
              aria-label="Fechar"
            >
              ✕
            </button>
          </div>
          <div className="equipment-consult-adhoc-map-title-row">
            <h3
              id="equipment-consult-adhoc-location-map-title"
              className="checkout-view-endereco-map-title"
            >
              {adhocLocationMap.address}
            </h3>
            {mapsOpenUrl ? (
              <a
                className="equipment-consult-adhoc-map-open-btn"
                href={mapsOpenUrl}
                target="_blank"
                rel="noopener noreferrer"
                title="Abrir no Google Maps"
              >
                <IconBrandGoogleMaps size={16} stroke={1.7} aria-hidden />
                <span>Abrir no Maps</span>
              </a>
            ) : null}
          </div>
          <div className="checkout-view-endereco-map-iframe-wrap">
            <iframe
              title="Localização do atendimento no Google Maps"
              src={mapsEmbedUrl}
              className="checkout-view-endereco-map-iframe"
            />
          </div>
        </div>
      </div>
    ) : null;

  return ReactDOM.createPortal(
    <>
      {overlay}
      {locationMapOverlay}
    </>,
    document.body
  );
};

export default EquipmentConsultSearchModal;
